import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  enableAnalytics,
  getConsent,
  type ConsentValue,
} from "@/lib/googleAnalytics";

declare global {
  interface Window {
    openCookieSettings?: () => void;
  }
}

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  const openBanner = useCallback(() => {
    const current = getConsent();
    setAnalyticsEnabled(current === "all");
    setShowSettings(true);
    setVisible(true);
  }, []);

  useEffect(() => {
    const consent: ConsentValue = getConsent();
    if (consent === "all") {
      enableAnalytics();
    } else if (consent === null) {
      setVisible(true);
    }
    window.openCookieSettings = openBanner;
    return () => {
      delete window.openCookieSettings;
    };
  }, [openBanner]);

  const acceptAll = useCallback(() => {
    localStorage.setItem("cookie-consent", "all");
    setVisible(false);
    setShowSettings(false);
    enableAnalytics();
  }, []);

  const acceptNecessary = useCallback(() => {
    localStorage.setItem("cookie-consent", "necessary");
    setVisible(false);
    setShowSettings(false);
  }, []);

  const saveSettings = useCallback(() => {
    localStorage.setItem("cookie-consent", analyticsEnabled ? "all" : "necessary");
    setVisible(false);
    setShowSettings(false);
    if (analyticsEnabled) enableAnalytics();
  }, [analyticsEnabled]);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 p-4 sm:p-6">
      <div className="mx-auto max-w-2xl rounded-lg border border-border bg-card p-4 sm:p-6 shadow-xl">
        <p className="font-sans text-sm text-foreground mb-1 font-medium">
          Cookie-Einstellungen
        </p>
        <p className="font-sans text-sm text-muted-foreground mb-4">
          Diese Website verwendet technisch notwendige Cookies sowie optionale Cookies zur Analyse der Websitenutzung.
          Weitere Informationen finden Sie in unserer{" "}
          <Link to="/datenschutz" className="underline text-[hsl(14,25%,38%)] hover:text-[hsl(14,25%,30%)]">
            Datenschutzerklärung
          </Link>
          .
        </p>

        {showSettings && (
          <div className="mb-4 space-y-3 rounded-md border border-border bg-muted/30 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-sans text-sm font-medium text-foreground">Notwendige Cookies</p>
                <p className="font-sans text-xs text-muted-foreground">Erforderlich für den Betrieb der Website</p>
              </div>
              <span className="font-sans text-xs text-muted-foreground italic">Immer aktiv</span>
            </div>
            <div className="border-t border-border" />
            <div className="flex items-center justify-between">
              <div>
                <p className="font-sans text-sm font-medium text-foreground">Analyse-Cookies</p>
                <p className="font-sans text-xs text-muted-foreground">Helfen uns, die Nutzung der Website zu verstehen</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={analyticsEnabled}
                onClick={() => setAnalyticsEnabled(!analyticsEnabled)}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${analyticsEnabled ? "bg-primary" : "bg-muted-foreground/30"}`}
              >
                <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${analyticsEnabled ? "translate-x-4" : "translate-x-0"}`} />
              </button>
            </div>
            <Button size="sm" onClick={saveSettings} className="w-full mt-2 bg-[hsl(14,25%,38%)] hover:bg-[hsl(14,25%,30%)] text-white">
              Einstellungen speichern
            </Button>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
          {!showSettings && (
            <Button variant="ghost" size="sm" onClick={() => setShowSettings(true)}>
              Einstellungen bearbeiten
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={acceptNecessary}>
            Nur notwendige
          </Button>
          <Button size="sm" onClick={acceptAll} className="bg-[hsl(14,25%,38%)] hover:bg-[hsl(14,25%,30%)] text-white">
            Alle akzeptieren
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
